import { ROUTES } from '../lib/routes';
import { navigate } from '../router';
import { el } from '../utils/dom';
import { ev123EvcUrl } from '../lib/config';
import {
  RESOURCE_ITEMS,
  type ResourceCategory,
  type ResourceTab,
  resourceCategoryBadgeClasses,
  resourceCategoryLabel,
} from '../data/resourcesBoard';

const TABS: [ResourceTab, string][] = [
  ['all', '전체'],
  ['notice', '공지사항'],
  ['law', '법령정보'],
  ['docs', '업무자료실'],
  ['news', '관련뉴스'],
];

const TAB_HINTS: Record<ResourceTab, string> = {
  all: 'EV123 자료실의 전체 게시글을 확인합니다.',
  notice: 'EV123 서비스 안내와 주요 업데이트를 확인합니다.',
  law: '전기차충전기 설치 관련 법령과 제도 정보를 확인합니다.',
  docs: '관리사무소·건물 담당자가 활용할 수 있는 실무자료를 확인합니다.',
  news: '전기차충전기 보급, 보조사업, 시장동향 뉴스를 확인합니다.',
};

type ResourceRow = {
  id: string;
  category: ResourceCategory;
  title: string;
  summary: string;
  body: string;
  source_label?: string;
  source_url?: string;
  attachment_label?: string;
  attachment_url?: string;
  is_pinned?: boolean;
  /** API 게시 여부. 없으면 정적 샘플 등에서 상태 배지 생략 */
  is_published?: boolean;
  sort_order?: number;
  published_at?: string;
};

function staticFallbackRows(): ResourceRow[] {
  return RESOURCE_ITEMS.map((x) => ({
    id: x.id,
    category: x.category,
    title: x.title,
    summary: x.summary,
    body: x.detail,
  }));
}

function isResourceCategory(v: string): v is ResourceCategory {
  return v === 'notice' || v === 'law' || v === 'docs' || v === 'news';
}

async function fetchPublishedRows(tab: ResourceTab): Promise<ResourceRow[]> {
  const qs = tab === 'all' ? '' : `?category=${encodeURIComponent(tab)}`;
  const url = ev123EvcUrl(`/resources${qs}`);
  try {
    const r = await fetch(url, { method: 'GET' });
    if (!r.ok) throw new Error('bad status');
    const j: unknown = await r.json();
    if (!Array.isArray(j)) throw new Error('bad shape');
    const out: ResourceRow[] = [];
    for (const raw of j) {
      if (!raw || typeof raw !== 'object') continue;
      const o = raw as Record<string, unknown>;
      const id = o.id;
      const cat = o.category;
      const title = o.title;
      const body = o.body;
      if (typeof id !== 'number' || typeof cat !== 'string' || !isResourceCategory(cat)) continue;
      if (typeof title !== 'string' || typeof body !== 'string') continue;
      const sortOrder = typeof o.sort_order === 'number' ? o.sort_order : 0;
      const publishedAt = typeof o.published_at === 'string' ? o.published_at : '';
      const isPub = typeof o.is_published === 'boolean' ? o.is_published : undefined;
      out.push({
        id: String(id),
        category: cat,
        title,
        summary: typeof o.summary === 'string' ? o.summary : '',
        body,
        source_label: typeof o.source_label === 'string' ? o.source_label : '',
        source_url: typeof o.source_url === 'string' ? o.source_url : '',
        attachment_label: typeof o.attachment_label === 'string' ? o.attachment_label : '',
        attachment_url: typeof o.attachment_url === 'string' ? o.attachment_url : '',
        is_pinned: Boolean(o.is_pinned),
        is_published: isPub,
        sort_order: sortOrder,
        published_at: publishedAt,
      });
    }
    return out;
  } catch {
    const fb = staticFallbackRows();
    if (tab === 'all') return fb;
    return fb.filter((x) => x.category === tab);
  }
}

function formatPublishedAt(raw?: string): string {
  if (!raw?.trim()) return '';
  const d = new Date(raw.trim());
  if (Number.isNaN(d.getTime())) return raw.trim();
  try {
    return new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
  } catch {
    return raw.trim();
  }
}

function filterBySearch(items: ResourceRow[], q: string): ResourceRow[] {
  const t = q.trim().toLowerCase();
  if (!t) return items;
  return items.filter((i) => {
    const hay = `${i.title}\n${i.summary}\n${i.body}`.toLowerCase();
    return hay.includes(t);
  });
}

function sortRows(rows: ResourceRow[]): ResourceRow[] {
  return [...rows].sort((a, b) => {
    const pa = a.is_pinned ? 1 : 0;
    const pb = b.is_pinned ? 1 : 0;
    if (pa !== pb) return pb - pa;
    const sa = a.sort_order ?? 0;
    const sb = b.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    const ta = a.published_at ?? '';
    const tb = b.published_at ?? '';
    if (ta !== tb) return tb.localeCompare(ta);
    return a.title.localeCompare(b.title, 'ko');
  });
}

function showResourceDetail(item: ResourceRow): void {
  const backdrop = el('div', {
    className: 'ev123-modal-backdrop',
    role: 'dialog',
    'aria-modal': 'true',
  });
  const modal = el('div', { className: 'ev123-modal ev123-modal--resource' });
  const title = el('h2', { className: 'ev123-modal__title', text: item.title });

  const bodyWrap = el('div', { className: 'ev123-resource-detail' });
  const pre = el('pre', { className: 'ev123-resource-detail__body', text: item.body });

  const links = el('div', { className: 'ev123-resource-detail__links' });
  if (item.source_url && /^https?:\/\//i.test(item.source_url)) {
    const lab = item.source_label?.trim() || '출처';
    links.appendChild(
      el('p', { className: 'ev123-resource-detail__link-row' }, [
        el('span', { className: 'ev123-resource-detail__link-label', text: `${lab}: ` }),
        (() => {
          const a = el('a', {
            className: 'ev123-resource-detail__link',
            href: item.source_url,
            target: '_blank',
            rel: 'noopener noreferrer',
            text: item.source_url,
          }) as HTMLAnchorElement;
          return a;
        })(),
      ])
    );
  }
  if (item.attachment_url && /^https?:\/\//i.test(item.attachment_url)) {
    const lab = item.attachment_label?.trim() || '첨부';
    links.appendChild(
      el('p', { className: 'ev123-resource-detail__link-row' }, [
        el('span', { className: 'ev123-resource-detail__link-label', text: `${lab}: ` }),
        (() => {
          const a = el('a', {
            className: 'ev123-resource-detail__link',
            href: item.attachment_url,
            target: '_blank',
            rel: 'noopener noreferrer',
            text: item.attachment_url,
          }) as HTMLAnchorElement;
          return a;
        })(),
      ])
    );
  }

  bodyWrap.append(pre);
  if (links.childElementCount) bodyWrap.appendChild(links);

  const actions = el('div', { className: 'ev123-modal__actions' });
  const btn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--sm',
    text: '닫기',
  }) as HTMLButtonElement;

  const close = (): void => {
    backdrop.remove();
  };
  btn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  actions.appendChild(btn);
  modal.append(title, bodyWrap, actions);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  btn.focus();
}

function filterItems(tab: ResourceTab, all: ResourceRow[]): ResourceRow[] {
  const base = tab === 'all' ? all : all.filter((x) => x.category === tab);
  return sortRows(base);
}

function renderRows(container: HTMLElement, tab: ResourceTab, rows: ResourceRow[], searchQ: string): void {
  container.replaceChildren();
  const items = filterBySearch(sortRows(filterItems(tab, rows)), searchQ);
  if (items.length === 0) {
    container.appendChild(
      el('p', {
        className: 'ev123-card__desc',
        style: 'margin:1rem 0',
        text: '표시할 게시글이 없습니다.',
      })
    );
    return;
  }
  for (const item of items) {
    const row = el('article', { className: 'ev123-resource-row' });
    const open = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--ghost ev123-btn--sm ev123-btn--pill ev123-resource-row__open',
    }) as HTMLButtonElement;
    open.textContent = '내용 보기';
    open.addEventListener('click', () => {
      showResourceDetail(item);
    });
    const dateStr = formatPublishedAt(item.published_at);
    const badgeRow = el('div', { className: 'ev123-resource-row__badges' });
    badgeRow.appendChild(
      el('span', {
        className: resourceCategoryBadgeClasses(item.category),
        text: resourceCategoryLabel(item.category),
      }),
    );
    if (item.is_pinned) {
      badgeRow.appendChild(el('span', { className: 'ev123-badge ev123-badge--pinned', text: '상단 고정' }));
    }
    if (item.is_published === false) {
      badgeRow.appendChild(el('span', { className: 'ev123-badge ev123-badge--draft', text: '비공개' }));
    } else if (item.is_published === true) {
      badgeRow.appendChild(el('span', { className: 'ev123-badge ev123-badge--published', text: '게시' }));
    }
    row.append(
      badgeRow,
      el('h3', { className: 'ev123-resource-row__title', text: item.title }),
      el('p', { className: 'ev123-resource-row__summary', text: item.summary }),
      dateStr
        ? el('p', { className: 'ev123-resource-row__date', text: `등록일 ${dateStr}` })
        : el('p', { className: 'ev123-resource-row__date ev123-resource-row__date--empty', text: '등록일 —' }),
      el('div', { className: 'ev123-resource-row__actions' }, [open])
    );
    container.appendChild(row);
  }
}

export async function renderResourcesPage(root: HTMLElement): Promise<void> {
  const wrap = el('div', { className: 'ev123-page-resources' });

  wrap.append(
    el('div', { className: 'ev123-page-head ev123-page-head--archive' }, [
      el('div', { className: 'ev123-container' }, [
        el('p', { className: 'ev123-portal-eyebrow', text: 'EV123 전문자료' }),
        el('h1', {
          className: 'ev123-page-head__title',
          text: '전기차충전기 설치 자료실',
        }),
        el('p', {
          className: 'ev123-page-head__lead',
          text: '공지사항, 법령정보, 업무자료, 관련뉴스를 한곳에서 확인할 수 있습니다.',
        }),
        el('p', {
          className: 'ev123-page-head__note ev123-card__desc',
          text: '법령·보조사업 조건은 시행 시점과 지자체 공고에 따라 달라질 수 있으니, 신청 전 최신 공고와 담당기관 확인이 필요합니다.',
        }),
      ]),
    ])
  );

  const container = el('div', { className: 'ev123-container ev123-stack' });

  const searchWrap = el('div', { className: 'ev123-resources-toolbar' });
  const searchInput = el('input', {
    className: 'ev123-input ev123-resources-search__input',
    type: 'search',
    placeholder: '제목·요약으로 검색',
    'aria-label': '자료실 검색',
  }) as HTMLInputElement;
  searchWrap.appendChild(searchInput);

  const tabBar = el('div', {
    className: 'ev123-tabs',
    role: 'tablist',
    'aria-label': '자료실 구분',
  });
  const tabHint = el('p', {
    className: 'ev123-card__desc',
    style: 'margin:0 0 0.75rem',
  });
  const listCard = el('div', { className: 'ev123-card ev123-card--resource-list' });
  const listBody = el('div', { className: 'ev123-resource-list' });

  let active: ResourceTab = 'all';
  const tabButtons: HTMLButtonElement[] = [];
  let cachedRows: ResourceRow[] = [];
  let loadSeq = 0;
  let searchQuery = '';

  function syncTabs(): void {
    for (let i = 0; i < tabButtons.length; i++) {
      const b = tabButtons[i]!;
      const tab = TABS[i]![0];
      const on = tab === active;
      b.classList.toggle('ev123-tab--active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    tabHint.textContent = TAB_HINTS[active];
  }

  async function reloadAll(): Promise<void> {
    const seq = ++loadSeq;
    listBody.replaceChildren(
      el('p', { className: 'ev123-card__desc', style: 'margin:1rem 0', text: '불러오는 중…' })
    );
    const allTab = await fetchPublishedRows('all');
    if (seq !== loadSeq) return;
    cachedRows = allTab;
    syncTabs();
    renderRows(listBody, active, cachedRows, searchQuery);
  }

  for (const [tab, label] of TABS) {
    const b = el('button', {
      type: 'button',
      className: 'ev123-tab',
      role: 'tab',
      'aria-selected': tab === active ? 'true' : 'false',
    }) as HTMLButtonElement;
    b.textContent = label;
    b.addEventListener('click', () => {
      active = tab;
      syncTabs();
      renderRows(listBody, active, cachedRows, searchQuery);
    });
    tabButtons.push(b);
    tabBar.appendChild(b);
  }

  syncTabs();
  listCard.appendChild(listBody);
  searchInput.addEventListener('input', () => {
    searchQuery = searchInput.value;
    renderRows(listBody, active, cachedRows, searchQuery);
  });
  container.append(searchWrap, tabBar, tabHint, listCard);

  const cta = el('div', {
    className: 'ev123-card ev123-card--cta ev123-card--cta-panel ev123-resources-cta',
    style: 'margin-top:1.5rem',
  });
  cta.append(
    el('h2', { className: 'ev123-card__title', text: '자료를 확인해도 판단이 어려우신가요?' }),
    el('p', {
      className: 'ev123-card__desc',
      text: '전기차충전기 설치 기준, 운영방식, 교체·이전 필요 여부는 건물 조건에 따라 달라질 수 있습니다. EVOS TEAM이 현장 조건에 맞춰 상담을 도와드립니다.',
    }),
    el('div', { className: 'ev123-hero__actions ev123-hero__actions--tight' }, [
      (() => {
        const b = el('button', {
          type: 'button',
          className: 'ev123-btn ev123-btn--primary ev123-btn--pill',
        }) as HTMLButtonElement;
        b.textContent = '설치 상담 신청';
        b.addEventListener('click', () => navigate(ROUTES.consult));
        return b;
      })(),
      (() => {
        const b = el('button', {
          type: 'button',
          className: 'ev123-btn ev123-btn--secondary ev123-btn--pill',
        }) as HTMLButtonElement;
        b.textContent = '충전기 설치 계산';
        b.addEventListener('click', () => navigate(ROUTES.newInstall));
        return b;
      })(),
    ])
  );

  container.appendChild(cta);
  wrap.appendChild(container);
  root.appendChild(wrap);

  await reloadAll();
}
